/**
 * Multi-tenant membership hooks. Powers the sidebar tenant-switcher
 * dropdown — the user picks one of their memberships and gets a fresh
 * JWT pair scoped to that tenant.
 *
 * After a successful switch, we overwrite localStorage with the new
 * tokens, clear React Query's cache (tenant-scoped data is now wrong),
 * and force a hard reload. Reload is the simplest way to guarantee the
 * sidebar / topbar re-evaluate `useAuth()` with the new claims —
 * triggering 50 partial re-renders + invalidations would be both
 * slower and bug-prone.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { setTokens } from '@/lib/auth';

export type TenantType = 'platform' | 'reseller' | 'customer';

export interface Membership {
  tenant_id: number;
  tenant_name: string;
  tenant_type: TenantType;
  role: string;
}

export interface MembershipsResponse {
  items: Membership[];
}

export interface AuthTokensResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

const MEMBERSHIPS_KEY = ['auth', 'memberships'] as const;

export function useMemberships() {
  return useQuery({
    queryKey: MEMBERSHIPS_KEY,
    queryFn: () => api.get<MembershipsResponse>('/v1/auth/memberships'),
    // Membership lists rarely change in a session — once per app boot
    // is plenty. The dropdown stays responsive on second open without
    // a network round-trip.
    staleTime: 5 * 60_000,
  });
}

export interface UseSwitchTenantOptions {
  /** Where to land after the switch. Defaults to a same-page reload —
   *  right for the in-app sidebar switcher (the user stays where they
   *  were). The login picker passes "/" so HomeRedirect can route the
   *  newly-scoped session to /admin/dashboard or /dashboard. */
  redirectTo?: string;
}

export function useSwitchTenant(options?: UseSwitchTenantOptions) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantID: number) =>
      api.post<AuthTokensResponse>('/v1/auth/switch-tenant', { tenant_id: tenantID }),
    onSuccess: (tokens) => {
      setTokens(tokens.access_token, tokens.refresh_token);
      // Clear *everything* — every query was scoped to the previous
      // tenant via the JWT and is now wrong. A full document load is
      // the cheapest way to also reset component state (selected rows,
      // modal state, etc.) that React Query alone wouldn't reach.
      qc.clear();
      if (options?.redirectTo) {
        window.location.href = options.redirectTo;
      } else {
        window.location.reload();
      }
    },
  });
}
